import { FC, useContext } from "react";
import { useTranslation } from "react-i18next";

import { Grid, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Subtitle, Title } from "capitalrx-components-2.0/lib/typography";

import { BlankPageWrapper, SectionPanel } from "@/App/components";
import { FindBestPriceWidget } from "@/App/components";
import { ExperienceContext } from "@/App/context";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { PlanContributionsWidget } from "@/App/pages";
import { useIsDiscountUser, useIsMedicareUser } from "@/App/hooks";

import { PharmacySearchCard } from "../PharmacySearch";
import { AdditionalServicesTiles } from "./AdditionalServicesTiles";
import { JudiCareTile } from "./JudiCareTile";
import { NeedHelpTile } from "./NeedHelpTile";
import { QuickActions } from "./QuickActions";
import { RecentActivity } from "../RecentActivity/RecentActivity";

const TRANSLATION_PATH = "DASHBOARD.recentActivityDashboard";

export const RecentActivityDashboardPage: FC = () => {
  const { t: translate } = useTranslation();
  const { memberEligibility } = useContext(MemberEligibilityContext);
  const {
    hidePlanContributions,
    hideAccumulators,
    hideFindBestPricePage,
    hideNearbyPharmaciesPage,
  } = useContext(ExperienceContext);
  const { isMedicareUser } = useIsMedicareUser();
  const { isDiscountUser } = useIsDiscountUser();

  const { leftColumnWidth, rightColumnWidth } = useResponsive({
    leftColumnWidth: {
      desktop: 8,
      tablet: 12,
      mobile: 12,
    },
    rightColumnWidth: {
      desktop: 4,
      tablet: 12,
      mobile: 12,
    },
  });

  return (
    <BlankPageWrapper>
      <Stack spacing="large">
        {/* Welcome Header */}
        <Title testId="welcome-header">
          {translate(`${TRANSLATION_PATH}.welcomeHeader`)}
          {memberEligibility.member.first_name || ""}
        </Title>

        {/* Quick Actions */}
        <QuickActions />

        {/* Two-Column Layout */}
        <Grid style={{ columnGap: "2rem" }}>
          {/* Left Column - Recent Activity */}
          <Grid.Span columns={leftColumnWidth}>
            <Stack spacing="medium">
              <RecentActivity />

              {/* JudiCare Tile */}
              <JudiCareTile />
            </Stack>
          </Grid.Span>

          {/* Right Column - Widgets */}
          <Grid.Span columns={rightColumnWidth}>
            <Stack spacing="large">
              {!hidePlanContributions && !isMedicareUser && !isDiscountUser && !hideAccumulators && (
                <Stack spacing="small">
                  <Subtitle testId="plan-contributions-title">{translate(`${TRANSLATION_PATH}.planContributionsTitle`)}</Subtitle>
                  <SectionPanel>
                    <PlanContributionsWidget />
                  </SectionPanel>
                </Stack>
              )}

              {/* Find Best Price */}
              {!hideFindBestPricePage && (
                <Stack spacing="small">
                  <Subtitle testId="find-best-price-title">{translate(`${TRANSLATION_PATH}.findBestPriceTitle`)}</Subtitle>
                  <SectionPanel>
                    <FindBestPriceWidget />
                  </SectionPanel>
                </Stack>
              )}

              {/* Find Nearby Pharmacies */}
              {!hideNearbyPharmaciesPage && (
                <PharmacySearchCard />
              )}

              {/* Need Help Tile */}
              <NeedHelpTile />
            </Stack>
          </Grid.Span>
        </Grid>

        {/* Additional Services (Costco) - Full Width */}
        <AdditionalServicesTiles />
      </Stack>
    </BlankPageWrapper>
  );
};
